import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Formik, FormikActions } from 'formik';
import { CreateLocationParams } from 'oh-main/web/frontend/global/modules/Locations/api/types/CreateLocationParams';
import { Divider, Typography } from '@mui/material';

import ModalWindowContent from '@ui-lib/components/ModalWindow/ModalWindowContent';
import { useSnackbarContext } from '@ui-lib/components/Snackbar/SnackbarProvider';
import { createSx } from '@ui-lib/utils';
import useManualRequest from '@web-shared/hooks/useManualRequest';
import HttpError from '@web-shared/services/http-service/HttpError';

import { validationSchema } from '../../../../../../../../oh-main/web/frontend/global/modules/Locations/pages/LocationBuilder/consts';
import {
  convertFormLocationsValuesForSubmit,
} from '../../../../../../../../oh-main/web/frontend/global/modules/Locations/pages/LocationBuilder/utils/index';
import {
  createLocation,
  getNearbyLocations,
} from '../../../../../../../../oh-main/web/frontend/global/modules/Locations/api/';

import { LOCATION_TYPE } from '../../../../../../../../oh-main/web/frontend/global/modules/Locations/api/enums/location-types';

import { useAppSelector } from '../../../../../../../redux/types';
import { LocationFormParams, FIELD_NAMES, initialValues } from './const';
import MapboxAutocomplete from './components/MapboxAutocomplete';
import LocationForm from './components/LocationForm';
import NearbyLocationsPopup, { NearbyLocationRef } from './components/NearbyLocationsPopup';

interface CreateLocationContentProps {
  onLocationCreated: (location: { id: number, label: string }) => void;
  onClose: () => void;
}

const sx = createSx({
  divider: {
    mt: -1,
  },
});

const CreateLocationContent = ({ onLocationCreated, onClose }: CreateLocationContentProps) => {
  const { t } = useTranslation();
  const portalId = useAppSelector((state) => state.userCompanies.companies[0].workspaces[0].portals[0].id);
  const { trigger: createLocationRequest } = useManualRequest(createLocation);
  const { trigger: fetchNearbyLocations } = useManualRequest(getNearbyLocations);
  const nearbyLocationsModal = useRef<NearbyLocationRef>(null);

  const { showErrorSnackbar } = useSnackbarContext();
  const initValues: LocationFormParams = {
    ...initialValues,
    'mapbox_location': null,
    [FIELD_NAMES.LOCATION_TYPE]: LOCATION_TYPE.STANDARD,
    [FIELD_NAMES.PORTAL]: {
      id: portalId,
      label: '',
    }
  };

  const createNewLocation = useCallback(async (
    values: LocationFormParams,
    setSubmitting: FormikActions<LocationFormParams>['setSubmitting'],
    setErrors: FormikActions<LocationFormParams>['setErrors'],
  ) => {
    try {
      const params = convertFormLocationsValuesForSubmit(values) as CreateLocationParams;
      setSubmitting(true);
      const location = await createLocationRequest(params);
      onLocationCreated({
        id: location.id,
        label: location.name
      });
      onClose();
    } catch (err) {
      if (HttpError.isHttpError(err)) {
        showErrorSnackbar({ message: err.message });
        if (err.errors) {
          setErrors(err.errors as Record<string, string>);
        }
      }
    }
    setSubmitting(false);
  }, [showErrorSnackbar]);

  const hanleFormSubmit = async (
    values: LocationFormParams,
    actions: FormikActions<LocationFormParams>
  ) => {
    const params = convertFormLocationsValuesForSubmit(values) as CreateLocationParams;
    actions.setSubmitting(true);
    try {
      const nearbyLocations = await fetchNearbyLocations({
        external_portal_id: portalId,
        lat: params.address.latitude!,
        lng: params.address.longitude!,
      });

      if (nearbyLocations.length > 0) {
        actions.setSubmitting(false);
        nearbyLocationsModal.current?.showNearbyLocations(nearbyLocations);
      } else {
        createNewLocation(values, actions.setSubmitting, actions.setErrors);
      }
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Formik
      onSubmit={hanleFormSubmit}
      initialValues={initValues}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, dirty, setSubmitting, setErrors, values }) => {
        const disableRegularFields = values.mapbox_location !== null;
        return (
          <ModalWindowContent
            modalMaxHeight='750px'
            title={t('label_createNewLocation')}
            submitBtnLabel={t('create')}
            processing={isSubmitting}
            onSubmit={handleSubmit}
            submitDisabled={!dirty || isSubmitting}
            onClose={onClose}
          >
            <>
              <MapboxAutocomplete />
              <Divider sx={sx.divider}>
                <Typography variant='overline'>
                  {t('caption_createLocationManually')}
                </Typography>
              </Divider>
              <LocationForm disableRegularFields={disableRegularFields} />
              <NearbyLocationsPopup
                modalRef={nearbyLocationsModal}
                onSelect={(location) => {
                  onLocationCreated(location);
                  onClose();
                }}
                onCreateNewLocation={() => createNewLocation(
                  values,
                  setSubmitting,
                  setErrors
                )}
              />
            </>
          </ModalWindowContent>
        );
      }}
    </Formik>
  );
};

export default CreateLocationContent;